import { DragDrop, DragRef, DropListRef, Point } from "@angular/cdk/drag-drop";
import { ElementRef, Injectable } from "@angular/core";
import { ViewFault } from "@docma-mm-core/fault/fault";
import { LoadStatusEnum } from "@docma-mm-core/model/load-state/load-state.enum";
import { Subscription, Subject } from "rxjs";
import { MmPlanDropZone } from "../../mm-faults/mm-plans-view/mm-plan-drop-zone";
import { DragMovedEvent, DragPreviewTemplate } from "../cdk";
import { MmDragContext } from "../mm-drag-context";
import { MmDropContext } from "../mm-drop-context";
import { MmDropZone } from "../mm-drop-zone";



@Injectable()
export class MmPinDragDropService {

  constructor(private readonly dragDrop: DragDrop) {
  }

  private _dragZone: DropListRef = null;
  private _previewTemplate: DragPreviewTemplate = null;
  private _dragFault: ViewFault;
  private _drag: DragRef;
  private _dragSubscription: Subscription;
  private _dropZones: MmDropZone[] = [];
  private _currentDropZone: MmDropZone;

  private _dragContext: MmDragContext;
  private _dropContext: MmDropContext;

  private _dropZoneEnteredSubject = new Subject<MmPlanDropZone>();
  public onDropZoneEntered = this._dropZoneEnteredSubject.asObservable();

  private _dragStartedSubject = new Subject<MmDragContext>();
  public onDragStarted = this._dragStartedSubject.asObservable();
  private _dragEndedSubject = new Subject<MmDragContext>();
  public onDragEnded = this._dragEndedSubject.asObservable();

  private _droppedSubject = new Subject<MmDropContext>();
  public onDropped = this._droppedSubject.asObservable();


  registerDragZone(element: ElementRef<HTMLElement> | HTMLElement) {
    this._dragZone?.dispose();
    this._dragZone = this.dragDrop.createDropList(element);
    this._dragZone.sortingDisabled = true;
  }

  registerDropZone(pinDropZone: MmDropZone) {
    this._dropZones = [pinDropZone];
    this.setDragDisabled();
  }
  unregisterDropZone(pinDropZone: MmDropZone) {
    //TODO
  }


  registerDragPreview(template: DragPreviewTemplate) {
    this._previewTemplate = template;
  }
  public get dragFault(): ViewFault {
    return this._dragFault;
  }

  public get isDragging(): boolean {
    return !!this._dragZone?.isDragging();
  }

  public registerFaultDrag(fault: ViewFault, element: ElementRef<HTMLElement> | HTMLElement) {
    this.unregisterFaultDrag();
    this._dragFault = fault;
    this.registerDrag(element);
  }

  public unregisterFaultDrag() {
    this._dragFault = null;
    this._dragContext = null;
    this._dragSubscription?.unsubscribe();
    this._drag?.dispose();
  }
  private setDragDisabled() {
    if (this._drag) {
      this._drag.disabled = !(this._dropZones || []).some(dz => !dz.disabled);
    }
  }
  private registerDrag(element: ElementRef<HTMLElement> | HTMLElement) {
    if (!this._dragZone) {
      return;
    }

    this._drag = this.dragDrop.createDrag(element);
    this._dragContext = { data: this._dragFault } as MmDragContext;
    if (this._previewTemplate) {
      this._previewTemplate.context = {
        $implicit: this._dragContext
      };
      this._drag.withPreviewTemplate(this._previewTemplate);
    }
    this.setDragDisabled();
    this._dragZone.withItems([this._drag]);
    this._dragSubscription = new Subscription();

    this._dragSubscription.add(this._drag.moved.subscribe(event => {
      try {
        this.cdkDragMoved(event);
      } catch (ex) {
        console.log(ex);
      }
    }));
    this._dragSubscription.add(this._drag.ended.subscribe(event => {
      try {
        this.cdkDragEnded();
      } catch (ex) {
        console.log(ex);
      }
    }));
    this._dragSubscription.add(this._drag.dropped.subscribe(event => {
      try {
        this.cdkDropped(event.dropPoint);
      } catch (ex) {
        console.log(ex);
      }
    }));

    this._dragSubscription.add(this._drag.started.subscribe(event => {
      try {
        this.cdkDragStarted();
      } catch (ex) {
        console.log(ex);
      }
    }));
  }
  private cdkDropped(dropPoint: Point) {
    if (this._dropContext) {
      this._dropContext.dropPoint = dropPoint;
      this._droppedSubject.next(this._dropContext);
    }
  }
  private cdkDragEnded() {
    if (this._dragContext.canDrop) {
      this._dropContext = {
        dropZone: this._currentDropZone,
        data: this._dragContext.data,
      };
    } else {
      this._dropContext = null;
    }
    document.body.classList.remove('mydocma-mm-pin-dragging');
    this.setCurrentDropZone(null);
    this._dragEndedSubject.next(this._dragContext);
  }
  private cdkDragStarted() {
    document.body.classList.add('mydocma-mm-pin-dragging');
    this._dragStartedSubject.next(this._dragContext);
  }
  private cdkDragMoved(event: DragMovedEvent) {
    if (this._dropZones?.length > 0) {
      const elementUnderPointer = document.elementFromPoint(event.pointerPosition.x, event.pointerPosition.y) as HTMLElement;
      if (elementUnderPointer) {
        const firstZone = this._dropZones[0];
        if (this.isInDropZone(firstZone, elementUnderPointer)) {
          this.setCurrentDropZone(firstZone);
          const can = firstZone.canDrop({ event: event.event, data: this._dragFault });
          this._dragContext.canDrop = can;
        } else {
          this.setCurrentDropZone(null);
        }
      }
    }
  }

  private isInDropZone(dropZone: MmDropZone, element: HTMLElement): boolean {
    if (!element) {
      return false;
    }
    if (dropZone.element === element) {
      return true;
    }
    return this.isInDropZone(dropZone, element.parentElement);
  }

  private setCurrentDropZone(zone: MmDropZone) {
    if (this._currentDropZone === zone) {
      return;
    }
    if (this._currentDropZone) {
      this._currentDropZone.element?.classList?.remove('mydocma-mm-drop-zone');
    }
    if (zone) {
      zone.element?.classList?.add('mydocma-mm-drop-zone');
    }
    if (this._dragContext) {
      this._dragContext.canDrop = false;
    }
    this._currentDropZone = zone;

  }

}
